import { graphql } from 'react-relay';
/* eslint-disable react/no-danger -- when updating this component, consider replacing dangerous jsx properties */
import type { ComponentType, FC } from 'react';
import React, { useContext, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import debounce from 'lodash/debounce';
import { styled } from '@compiled/react';
import escapeRegExp from 'lodash/escapeRegExp';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkSearchIcon from '@atlaskit/icon/core/search';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import type {
	GroupedOptionsType,
	OptionType,
	FormatOptionLabelMeta,
	DropdownIndicatorProps,
	MenuListComponentProps,
} from '@atlaskit/select/types';
import AkSelect from '@atlaskit/select/select';
import { components } from '@atlaskit/select/react-select';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import type { ForgeUISpaceSettingsExtensionType } from '@confluence/forge-ui/entry-points/types.ts';
import { getAppId } from '@confluence/forge-ui/entry-points/appData.ts';
import { extensionTitle } from '@confluence/forge-ui/entry-points/extensionTitle.ts';
import { SPACE_SETTINGS_FORGE_APPS } from '@confluence/named-routes/entry-points/routes.ts';
import type { RoutesContextType } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';

import { IntegrationsIconV2 } from '../landing-page/img/index.ts';
import {
	useSelectedNavigationWebItems,
	isForgeAppMatchingRoute,
	FORGE_SPACE_SETTINGS_NAVIGATION_PARENT_WEBITEM,
} from '../helpers/webItem.ts';
import { getWebItemIdMapToIcon } from '../helpers/icons.ts';
import { isNonNullable } from '../utils.ts';
import { useSpaceSettingsWebItemSectionsFromQuery } from '../web-item-sections/useSpaceSettingsWebItemSectionsFromQuery.tsx';

import type {
	SpaceSettingsFilterBarQuery as SpaceSettingsFilterBarQuery$data,
	SpaceSettingsFilterBarQueryVariables as SpaceSettingsFilterBarQuery$variables,
	SpaceSettingsFilterBarQuery_webItemSections_WebSection as WebSection,
} from './__types__/SpaceSettingsFilterBarQuery.ts';
import searchNoResults from './img/searchNoResults.svg';
import type { ForgeAppsProps } from './ForgeAppTabs.tsx';
import { ForgeAppsLoader } from './ForgeAppsLoader.tsx';
import { SpaceAccessFeedbackButton } from './SpaceAccessFeedbackButton.tsx';
import { useLazyQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useLazyQueryInterop.ts';
const SpaceSettingsFilterBarQuery = graphql`
	query SpaceSettingsFilterBarQuery($spaceKey: String) {
		webItemSections(location: "system.space.tools", key: $spaceKey) {
			id
			label
			cacheKey
			items {
				id
				completeKey
				label
				url
				icon {
					path
				}
			}
		}
		space(key: $spaceKey) {
			alias
			id
			key
			currentUser {
				isAdmin
			}
		}
	}
`;
const ANALYTICS_ACTION_SUBJECT = 'spaceSettingsFilterBar';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterBarContainer = styled.div({
	width: '250px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionInMenu = styled.div({
	margin: 0,
	padding: `${token('space.050')} ${token('space.100')}`,
});

const customSelectStyles = {
	group: (styles) => ({ ...styles, paddingTop: 0, paddingBottom: 0 }),
	control: (styles, state) => ({
		...styles,
		flexDirection: 'row-reverse',
		// B100 corresponds with primary75 theme color from react-select
		// This is the standard blue border color used by focused elements
		borderColor: state.isFocused ? token('color.border.focused') : token('color.border'),
		background: token('color.background.input'),
	}),
	// boost z index menu over some legacy SSR page HTML elements
	menu: (styles) => ({ ...styles, zIndex: 9 }),
};

const i18n = defineMessages({
	searchPlaceholderText: {
		id: 'space-settings.navigation.filter-bar.placeholder-text',
		defaultMessage: 'Jump to setting...',
		description:
			'Placeholder text for the search/filter bar in space settings - user can jump to specific setting page',
	},
	noOptionsMessage: {
		id: 'space-settings.navigation.filter-bar.empty-options',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "We couldn't find anything matching your search.",
		description: 'Message shown when the search for filtered space settings returns no results',
	},
	errorLoadingResults: {
		id: 'space-settings.navigation.filter-bar.error-loading',
		defaultMessage: 'Some links failed to load',
		description:
			'Message appearing in Space Settings search bar indicating that results may be incomplete',
	},
});

export type SpaceSettingsFilterBarProps = ForgeAppsProps & {
	source: string;
	includeForgeApps?: boolean;
	spaceAlias?: string | null;
};

/**
 * Renders Confluence Space Settings V2 filter bar, to allow searching/filtering for space
 * settings links to jump to (including Connect and Forge apps).
 * This filter bar is included both with the navigation on legacy monolith SSR space settings pages,
 * and on fully React SPA space settings pages (currently only the Forge app space settings page).
 */
export const SpaceSettingsFilterBar: FC<React.PropsWithChildren<SpaceSettingsFilterBarProps>> = ({
	includeForgeApps = true,
	spaceKey,
	spaceAlias,
	source,
	forgeAppId,
	forgeEnvId,
	forgeRoutePath,
}) => {
	// Forge apps will be loaded by a child component and set into state locally.
	const [forgeApps, setForgeApps] = useState<readonly ForgeUISpaceSettingsExtensionType[]>([]);
	const [startLoading, setStartLoading] = useState<boolean>(false);
	const [forgeLoadError, setForgeLoadError] = useState<Error>();
	const { location, matchRoute, push } = useContext<RoutesContextType>(RoutesContext);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [queryForWebItems, { data, loading, error: webItemQueryError }] = useLazyQueryInterop<
		SpaceSettingsFilterBarQuery$data,
		SpaceSettingsFilterBarQuery$variables
	>(SpaceSettingsFilterBarQuery, {
		variables: { spaceKey },
		errorPolicy: 'all',
	});
	const handleMenuOpen = () => {
		// load options if we do not already have data
		if (!loading && !data) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'search',
					actionSubjectId: ANALYTICS_ACTION_SUBJECT,
					source,
				},
			}).fire();

			void queryForWebItems();
			setStartLoading(true);
		}
	};

	const onLoadForgeError = (forgeError: Error) => {
		setForgeLoadError(forgeError);
	};

	const handleSelectLink = (item: OptionType | null) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: ANALYTICS_ACTION_SUBJECT,
				source,
				attributes: {
					linkId: item?.value,
				},
			},
		}).fire();
		item?.data.href && push(item.data.href);
	};

	const createForgeAppOptions = (parentWebItemSection: WebSection) =>
		forgeApps.length
			? forgeApps.filter(isNonNullable).map((forgeApp: ForgeUISpaceSettingsExtensionType) => {
					const appId = getAppId(forgeApp) ?? '';
					return {
						label: extensionTitle(forgeApp),
						value: appId,
						data: {
							href: SPACE_SETTINGS_FORGE_APPS.toUrl({
								spaceKey: spaceAlias || spaceKey,
								appId,
								envId: forgeApp.environmentId,
								forgeManifestRoute: forgeApp.properties?.route,
							}),
							forgeApp,
							parentLabel: parentWebItemSection.label,
						},
					};
				})
			: [];

	const webItemSections = useSpaceSettingsWebItemSectionsFromQuery(data);

	const allOptions: GroupedOptionsType<OptionType> =
		webItemSections?.filter(isNonNullable).map((webItemSection: WebSection) => ({
			label: webItemSection.label ?? undefined,
			id: webItemSection.id,
			options: webItemSection.items
				.filter(isNonNullable)
				.map((item) => ({
					label: item.label!,
					value: item.completeKey!,
					data: { href: item.url, parentLabel: webItemSection.label },
				}))
				// If we are in the correct top-level webItemSections, we can add on Forge apps
				// to the inner `options` array that we are currently creating
				.concat(
					webItemSection.id === FORGE_SPACE_SETTINGS_NAVIGATION_PARENT_WEBITEM
						? createForgeAppOptions(webItemSection)
						: [],
				),
		})) ?? [];

	const { innerNavigationWebItem } = useSelectedNavigationWebItems({
		matchRoute,
		location,
		webItemSections,
	});

	const formatOptionLabel = (
		{ label }: OptionType,
		{ inputValue }: FormatOptionLabelMeta<OptionType>,
	) => {
		// bold text in the option label that matches the search string, if any
		const optionTextWithMatchesHighlighted = inputValue
			? label.replace(new RegExp(inputValue, 'gi'), (highlighted) => `<b>${highlighted}</b>`)
			: label;
		return (
			<OptionInMenu>
				<span dangerouslySetInnerHTML={{ __html: optionTextWithMatchesHighlighted }} />
			</OptionInMenu>
		);
	};

	const filterOption = ({ label, value, data: optionData }: OptionType, inputValue: string) => {
		// Filter out the option for the space settings page we are currently on, if any.
		// If we are on a Forge app page we match the option through forge props passed through from navigation.
		// Otherwise, we compare against innerNavigationWebItem which matches the selected page by URL.
		const optionIsCurrentPage = optionData.data?.forgeApp
			? isForgeAppMatchingRoute(optionData.data.forgeApp, forgeAppId, forgeEnvId, forgeRoutePath)
			: innerNavigationWebItem?.completeKey === value;

		return (
			!optionIsCurrentPage &&
			// Filter options by search text (inputValue) - does it match either the option label or its parent?
			(label?.search(new RegExp(escapeRegExp(inputValue), 'i')) > -1 ||
				optionData.data?.parentLabel?.search(new RegExp(escapeRegExp(inputValue), 'i')) > -1)
		);
	};

	const fireTypeEvent = (searchTerm) => {
		if (searchTerm?.length) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'typed',
					actionSubject: 'searchTerm',
					actionSubjectId: ANALYTICS_ACTION_SUBJECT,
					source,
					attributes: {
						searchTermLength: searchTerm.length,
					},
				},
			}).fire();
		}
	};

	const handleType = debounce(fireTypeEvent, 200, { leading: false });

	const error = webItemQueryError ?? forgeLoadError;

	return (
		<Inline alignBlock="center" space="space.150">
			<SpaceAccessFeedbackButton />
			<FilterBarContainer>
				<AkSelect<OptionType, false>
					options={allOptions}
					id="space-settings-filter-bar"
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
					className="single-select"
					classNamePrefix="react-select"
					styles={customSelectStyles}
					maxMenuHeight={400}
					formatOptionLabel={formatOptionLabel}
					placeholder={<FormattedMessage {...i18n.searchPlaceholderText} />}
					label="Search settings"
					isLoading={loading}
					onInputChange={handleType}
					onMenuOpen={handleMenuOpen}
					onChange={handleSelectLink}
					filterOption={filterOption}
					// This is not a true "select" -- it's a tool for user to browse and search for their desired
					// space setting page. When the desired result is clicked, we redirect user to the URL of the selected
					// option. There is no reason for selected value to persist inside the component, either before
					// redirection or especially between SPA pageloads (e.g. switching between Forge app tabs), in which this
					// component will stay mounted. So "selected value" should consistently be empty.
					value={null}
					components={{
						DropdownIndicator: CustomDropdownIndicator,
						NoOptionsMessage: CustomNoOptionsMessage,
						GroupHeading: CustomGroupHeading,
						MenuList: (props) => <CustomMenuList error={error} {...props} />,
					}}
				/>
				{includeForgeApps && startLoading ? ( // 'includeForgeApps' prop provided just for testing purposes
					<ForgeAppsLoader
						onLoadForgeApps={setForgeApps}
						onError={onLoadForgeError}
						showLoadingErrorStates={false}
					/>
				) : null}
				{error ? ( // report error only once (here) as opposed to multiple times inside Select
					<ErrorDisplay error={error} />
				) : null}
			</FilterBarContainer>
		</Inline>
	);
};

// A few custom styled components for Select
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupLabel = styled.div({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	paddingLeft: token('space.250'),
	font: token('font.body.small'),
	background: token('color.background.neutral'),
	color: token('color.text.subtle'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupLabelText = styled.span({
	padding: `${token('space.150')} ${token('space.075')}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NoOptionsContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: token('space.400'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NoOptionsMessage = styled.div({
	color: token('color.text'),
	font: token('font.body'),
	width: '136px',
	textAlign: 'center',
	paddingTop: token('space.300'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledErrorContainer = styled.div({
	alignSelf: 'flex-end',
	padding: `${token('space.025')} 5px`,
	height: '20px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledErrorMessage = styled.span({
	font: token('font.body.small'),
	color: token('color.text.subtle'),
});

const CustomDropdownIndicator: ComponentType<DropdownIndicatorProps<OptionType>> = (props) => (
	<components.DropdownIndicator {...props}>
		<AkSearchIcon spacing="spacious" label="" />
	</components.DropdownIndicator>
);

const CustomNoOptionsMessage: FC<React.PropsWithChildren<{}>> = () => (
	<NoOptionsContainer>
		<img src={searchNoResults} width={125} alt="" />
		<NoOptionsMessage>
			<FormattedMessage {...i18n.noOptionsMessage} />
		</NoOptionsMessage>
	</NoOptionsContainer>
);

// GroupHeading component props are typed as "any" from AkSelect
const CustomGroupHeading: FC<React.PropsWithChildren<any>> = (props) => {
	const webItemIdMapToIcon = getWebItemIdMapToIcon();

	// custom icon per section / grouping, same as the space settings landing page
	const IconComponent = webItemIdMapToIcon[props.data.id] ?? IntegrationsIconV2;

	return (
		// override margin/padding so background color can cover entire group label row
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		<components.GroupHeading {...props} style={{ margin: 0, padding: 0 }}>
			<GroupLabel>
				<IconComponent width="24" height="24" />
				<GroupLabelText>{props.data.label}</GroupLabelText>
			</GroupLabel>
		</components.GroupHeading>
	);
};

const CustomMenuList: FC<
	React.PropsWithChildren<
		MenuListComponentProps<OptionType> & {
			error?: ApolloErrorInterop | Error;
		}
	>
> = ({ error, children, ...menuListComponentProps }) => (
	<components.MenuList {...menuListComponentProps}>
		{error ? ( // add warning to top of results dropdown if we failed to load some links
			<StyledErrorContainer>
				<StatusWarningIcon label="" color={token('color.icon.warning')} />
				<StyledErrorMessage>
					<FormattedMessage {...i18n.errorLoadingResults} />
				</StyledErrorMessage>
			</StyledErrorContainer>
		) : null}
		{children}
	</components.MenuList>
);
